/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Location
} from '@angular/common';
import {
	Component,
	ElementRef,
	HostListener,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	UntypedFormControl
} from '@angular/forms';
import {
	ActivatedRoute,
	Params,
	Router,
	UrlCreationOptions
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityLayoutTypeApiService
} from '@api/services/entities/entity-layout-type.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	ContentAnimation
} from '@shared/app-animations';
import {
	CommonTableComponent
} from '@shared/components/common-table/common-table.component';
import {
	AppEventConstants
} from '@shared/constants/app-event.constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	CommonTablePageDirective
} from '@shared/directives/common-table-page.directive';
import {
	OptionsFactory
} from '@shared/factories/options-factory';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ApiHelper
} from '@shared/helpers/api.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	ICommonTableColumn
} from '@shared/interfaces/application-objects/common-table-column.interface';
import {
	ICommonTable
} from '@shared/interfaces/application-objects/common-table.interface';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IKeyValuePair
} from '@shared/interfaces/application-objects/key-value-pair.interface';
import {
	IObjectSearch
} from '@shared/interfaces/application-objects/object-search.interface';
import {
	IOwnershipGuardComponent
} from '@shared/interfaces/application-objects/ownership-guard-component';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityLayoutType
} from '@shared/interfaces/entities/entity-layout-type.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	ResolverService
} from '@shared/services/resolver.service';
import {
	SessionService
} from '@shared/services/session.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	get
} from 'lodash-es';
import {
	DateTimeUnit
} from 'luxon';

/* eslint-enable max-len */

@Component({
	selector: 'insurance-policy-search',
	templateUrl: './insurance-policy-search.component.html',
	styleUrls: [
		'./insurance-policy-search.component.scss'
	],
	animations: [
		ContentAnimation
	]
})

/**
 * A component representing an insurance level policy search.
 *
 * @export
 * @extends {CommonTablePageDirective}
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @implements {IOwnershipGuardComponent}
 * @implements {IDynamicComponent<Component, any>}
 * @class InsurancePolicySearchComponent
 */
export class InsurancePolicySearchComponent
	extends CommonTablePageDirective
	implements OnInit, IOwnershipGuardComponent,
		OnInit, IDynamicComponent<Component, any>
{
	/**
	 * Creates an instance of the insurance policy search component.
	 *
	 * @param {Router} router
	 * The router to use for naviation.
	 * @param {Location} location
	 * The location service used to create and populate the url tree.
	 * @param {ActivatedRoute} route
	 * The activated route that opened this component.
	 * @param {ResolverService} resolver
	 * The resolver service used for dynamic logic and business rules.
	 * @param {EntityService} entityService
	 * The entity service used for data lookups.
	 * @param {SessionService} sessionService
	 * The session service used for user lookups.
	 * @param {OptionsFactory} optionsFactory
	 * The options factory used for dropdown option creation.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used for dynamic displays.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The api service used to load the entity instance data.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The api service used to load the entity instance data.
	 * @param {EntityLayoutTypeApiService} entityLayoutTypeApiService
	 * The entity layout type api service used for layout lookups.
	 * @memberof InsurancePolicySearchComponent
	 */
	 public constructor(
		public router: Router,
		public location: Location,
		public route: ActivatedRoute,
		public resolver: ResolverService,
		public entityService: EntityService,
		public sessionService: SessionService,
		public optionsFactory: OptionsFactory,
		public siteLayoutService: SiteLayoutService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public entityLayoutTypeApiService: EntityLayoutTypeApiService)
	{
		super(resolver);
	}

	/**
	 * Gets or sets the tooltip element reference.
	 *
	 * @type {ElementRef}
	 * @memberof InsurancePolicySearchComponent
	 */
	@ViewChild('Tooltip')
	public tooltip: ElementRef;

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof DrawerListDirective
	 */
	public context: IDynamicComponentContext<Component, any>;

	/**
	 * Gets or sets the table row count.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	public tableRowCount: number = 15;

	/**
	 * Gets or sets the data limit for the api promise.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	public dataLimit: number = 30;

	/**
	 * Gets or sets the list of all transaction entity types.
	 *
	 * @type {IEntityType[]}
	 * @memberof InsurancePolicySearchComponent
	 */
	public availableTransactionEntityTypes: IEntityType[];

	/**
	 * Gets or sets the loading value of transaction entity types.
	 *
	 * @type {boolean}
	 * @memberof InsurancePolicySearchComponent
	 */
	public loadingTransactionTypes: boolean = true;

	/**
	 * Gets or sets the sort order by.
	 *
	 * @type {string}
	 * @memberof InsurancePolicySearchComponent
	 */
	public orderBy: string = AppConstants.empty;

	/**
	 * Gets or sets the full length sort order by.
	 *
	 * @type {string}
	 * @memberof InsurancePolicySearchComponent
	 */
	public exactPropertyOrderBy: string = AppConstants.empty;

	/**
	 * Gets or sets the sort field.
	 *
	 * @type {string}
	 * @memberof InsurancePolicySearchComponent
	 */
	public sortField: string = AppConstants.commonProperties.effectiveDate;

	/**
	 * Gets or sets the sort order.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	public sortOrder: number = 1;

	/**
	 * Gets or sets the full page reserved height used to display the filter
	 * layout.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	public fullPageReservedHeight: number = 282;

	/**
	 * Gets or sets the table definitions for the standard table view.
	 *
	 * @type {ICommonTable}
	 * @memberof InsurancePolicySearchComponent
	 */
	public policySearchTableDefinitions: ICommonTable;

	/**
	 * Gets or sets the formly definition for the policy search.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof InsurancePolicySearchComponent
	 */
	public dynamicFormly: FormlyFieldConfig[] = [];

	/**
	 * Gets or sets the validity of the form.
	 *
	 * @type {boolean}
	 * @memberof InsurancePolicySearchComponent
	 */
	public validForm: boolean = true;

	/**
	 * Gets or sets the mode of the table. When in search mode, this will
	 * execute the API promise to gather data. This is enabled by using
	 * the search action on the API to request filtered data.
	 *
	 * @type {boolean}
	 * @memberof InsurancePolicySearchComponent
	 */
	public searchMode: boolean = false;

	/**
	 * Gets or sets the latest transaction set by product.
	 *
	 * @type {IKeyValuePair[]}
	 * @memberof InsurancePolicySearchComponent
	 */
	public latestTransactionsByProduct: IKeyValuePair[] = [];

	/**
	 * Gets or sets the default filters.
	 *
	 * @type {ICommonTable}
	 * @memberof InsurancePolicySearchComponent
	 */
	public defaultFilters: object =
		{
			selectedTransactionEntityTypes: [],
			createdDate: AppConstants.dateLimits.pastThirtyDays,
			status: AppConstants.any,
			type: AppConstants.any,
			keywordFilter: AppConstants.empty
		};

	/**
	 * Gets or sets the formly definition for the policy search data values.
	 *
	 * @type {ICommonTable}
	 * @memberof InsurancePolicySearchComponent
	 */
	public dataSet: { data: any } =
		<{ data: any }>
		{
			data: {
				...this.defaultFilters
			}
		};

	/**
	 * Gets the reserved filter form height when displaying this page
	 * in a tablet or smaller screen.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	private readonly tabletReservedScreenHeight: number = 550;

	/**
	 * Gets the reserved filter form height when displaying this page
	 * in a screen larger than a tablet.
	 *
	 * @type {number}
	 * @memberof InsurancePolicySearchComponent
	 */
	private readonly desktopReservedScreenHeight: number = 282;

	/**
	 * Gets the available url parameters.
	 *
	 * @type {any}
	 * @memberof InsurancePolicySearchComponent
	 */
	private readonly parameters:
	{
		selectedTransactionEntityTypes: string;
		createdDate: string;
		status: string;
		type: string;
		changedBy: string;
		createdBy: string;
		keywordFilter: string;
		orderBy: string;
		exactPropertyOrderBy: string;
		searchMode: string;
	} = {
			selectedTransactionEntityTypes: 'selectedTransactionEntityTypes',
			createdDate:'createdDate',
			status: 'status',
			type: 'type',
			changedBy: 'changedBy',
			createdBy: 'createdBy',
			keywordFilter: 'keywordFilter',
			orderBy: 'orderBy',
			exactPropertyOrderBy: 'exactPropertyOrderBy',
			searchMode: 'searchMode'
		};

	/**
	 * Gets the set of policy term data required columns.
	 *
	 * @type {any}
	 * @memberof InsurancePolicySearchComponent
	 */
	private readonly termColumnKeys: Set<string> =
		new Set(
			[
				'data.termEffectiveDate',
				'data.paymentPlanId',
				'data.renewalTerm',
				'data.writtenTotal'
			]);

	/**
	 * Handles the site layout change event which is called
	 * when the site layout service has altered it's variables.
	 *
	 * @memberof InsurancePolicySearchComponent
	 */
	@HostListener(
		AppEventConstants.siteLayoutChangedEvent)
	public siteLayoutChanged(): void
	{
		if (this.loadingTableDefinitions === true
			|| this.searchMode === false)
		{
			return;
		}

		let reservedHeight: number =
			this.policySearchTableDefinitions.fullPageReservedHeight;

		if (this.siteLayoutService.breakpointWidth <=
			AppConstants.layoutBreakpoints.tablet
			&& reservedHeight !== this.tabletReservedScreenHeight)
		{
			reservedHeight = this.tabletReservedScreenHeight;
		}
		else if (this.siteLayoutService.breakpointWidth >
			AppConstants.layoutBreakpoints.tablet
			&& reservedHeight !== this.desktopReservedScreenHeight)
		{
			reservedHeight = this.desktopReservedScreenHeight;
		}
		else
		{
			return;
		}

		this.loadingTableDefinitions = true;

		this.policySearchTableDefinitions.fullPageReservedHeight =
			reservedHeight;

		setTimeout(
			() =>
				this.loadingTableDefinitions = false,
			AppConstants.time.fiftyMilliseconds);
	}

	/**
	 * Handles the on initialization event.
	 * This will setup the table definitions and parameter handling
	 * for the policy search page.
	 *
	 * @async
	 * @memberof InsurancePolicySearchComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		if (!await this.isPageOwnershipAllowed())
		{
			EventHelper.dispatchNavigateToAccessDeniedEvent(
				this.location.path(),
				[
					'Entity.Type',
					'Entity.Version',
					'Entity.Layout'
				],
				'Access is required to at least one policy entity type '
					+ 'and version.');

			return;
		}

		this.route.queryParams.subscribe(
			async(parameters: Params) =>
			{
				const mappedRouteData: any =
					ObjectHelper.mapFromRouteData(
						parameters);

				this.dataSet.data.selectedTransactionEntityTypes =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[
							this.parameters.selectedTransactionEntityTypes])
						? mappedRouteData[
							this.parameters.selectedTransactionEntityTypes]
						: this.dataSet.data.selectedTransactionEntityTypes;
				this.dataSet.data.createdDate =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.createdDate])
						? mappedRouteData[this.parameters.createdDate]
						: this.dataSet.data.createdDate;
				this.dataSet.data.status =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.status])
						? mappedRouteData[this.parameters.status]
						: this.dataSet.data.status;
				this.dataSet.data.type =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.type])
						? mappedRouteData[this.parameters.type]
						: this.dataSet.data.type;
				this.dataSet.data.keywordFilter =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.keywordFilter])
						? mappedRouteData[this.parameters.keywordFilter]
						: this.dataSet.data.keywordFilter;
				this.orderBy =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.orderBy])
						? mappedRouteData[this.parameters.orderBy]
						: `${AppConstants.commonProperties.id} `
							+ `${AppConstants.sortDirections.descending}`;
				this.exactPropertyOrderBy =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.exactPropertyOrderBy])
						? mappedRouteData[this.parameters.exactPropertyOrderBy]
						: `${AppConstants.commonProperties.id} `
							+ `${AppConstants.sortDirections.descending}`;
				this.searchMode =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.searchMode])
						? JSON.parse(mappedRouteData[
							this.parameters.searchMode])
						: false;

				this.dataSet.data.changedBy =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.changedBy])
						? mappedRouteData[this.parameters.changedBy]
						: StringHelper.fromNameObject(
							this.sessionService.user.data);
				this.dataSet.data.createdBy =
					!AnyHelper.isNullOrEmpty(
						mappedRouteData[this.parameters.createdBy])
						? mappedRouteData[this.parameters.createdBy]
						: StringHelper.fromNameObject(
							this.sessionService.user.data);
			});

		this.sortField =
			this.orderBy.split(
				AppConstants.characters.space)[0];

		this.sortOrder =
			this.orderBy.split(
				AppConstants.characters.space)[1] ===
					AppConstants.sortDirections.ascending
				? 1
				: -1;

		this.context =
			<IDynamicComponentContext<Component, any>>
			{
				source: this,
				data: {}
			};

		await this.setupPageVariables();
		this.setupTableDefinitions();
		this.updateUrlQuery();
	}

	/**
	 * Implements the ownership guard interface.
	 * This will calculate page ownership permissions.
	 *
	 * @async
	 * @returns {Promise<boolean>}
	 * A value signifying whether or not access is allowed to this page.
	 * @memberof InsurancePolicySearchComponent
	 */
	public async isPageOwnershipAllowed(): Promise<boolean>
	{
		const fullEntityLayoutType: IEntityLayoutType =
			await this.entityLayoutTypeApiService.getSingleQueryResult(
				AppConstants.commonProperties.name
					+ ` eq '${AppConstants.layoutTypes.full}'`,
				AppConstants.empty,
				true);

		if (AnyHelper.isNull(fullEntityLayoutType))
		{
			return false;
		}

		return this.entityService.verifyWildcardEntityTypeAccess(
			AppConstants.commonProperties.group
				+ '.StartsWith(\''
				+ InsuranceConstants.policyTermTransactionPrefix
				+ '\') eq true',
			fullEntityLayoutType);
	}

	/**
	 * Captures validity changes sent from the dynamic formly component.
	 *
	 * @param {boolean} valid
	 * Whether or not the current form is valid.
	 * @memberof InsurancePolicySearchComponent
	 */
	 public formValidityChange(
		valid: boolean): void
	{
		this.validForm = valid;
	}

	/**
	 * Handles a click of the search action.
	 *
	 * @memberof InsurancePolicySearchComponent
	 */
	public search(): void
	{
		const filter: string =
			this.compilePrimaryFilter();

		this.searchMode = true;
		this.updateUrlQuery();

		setTimeout(
			() =>
				this.commonTableContext.source
					.filterCriteriaChanged(filter),
			AppConstants.time.oneHundredMilliseconds);
	}

	/**
	 * Sets the page level variables.
	 *
	 * @async
	 * @memberof InsurancePolicySearchComponent
	 */
	public async setupPageVariables(): Promise<void>
	{
		const assetCharacteristics: string =
			'data.assets[0].characteristics';
		const interestCharacteristics: string =
			'data.interests[0].characteristics';
		const userDisplayName: string =
			StringHelper.fromNameObject(
				this.sessionService.user.data);

		this.availableTransactionEntityTypes =
			await ApiHelper.getFullDataSet(
				this.entityTypeApiService,
				'name.StartsWith('
					+ `'${InsuranceConstants
						.policyTermTransactionPrefix}')`,
				AppConstants.empty);

		this.dynamicFormly =
			<FormlyFieldConfig[]>
			[
				{
					key: 'data.selectedTransactionEntityTypes',
					type: FormlyConstants.customControls.customMultiSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Products',
						placeholder: 'Select product(s)',
						required: true,
						options:
							this.availableTransactionEntityTypes
								.map(
									(entityType: IEntityType) =>
									{
										const splitEntitType: string[] =
											entityType.name.split(
												AppConstants.characters.period);

										return <IDropdownOption>
											{
												label: splitEntitType[
													splitEntitType.length - 1],
												value: entityType.group
											};
									})
					},
					validators: {
						validProductSelection: {
							expression: ((
								control: UntypedFormControl,
								_field: FormlyFieldConfig) =>
								control.value.length > 0),
							message:
								AppConstants.empty
						}
					}
				},
				{
					key: 'data.createdDate',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Created Date',
						placeholder: 'Select date range',
						required: false,
						options: this.optionsFactory.mapStringOptions(
							[
								AppConstants.dateLimits
									.pastThirtyDays,
								AppConstants.dateLimits
									.pastNinetyDays,
								AppConstants.dateLimits
									.pastOneHundredAndEightyDays
							],
							true)
					}
				},
				{
					key: 'data.type',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Type',
						placeholder: 'Select transaction type',
						required: false,
						options:
							this.optionsFactory.mapStringOptions(
								[
									InsuranceConstants.transactionTypes
										.newBusiness,
									InsuranceConstants.transactionTypes
										.renewal,
									InsuranceConstants.transactionTypes
										.endorsement,
									InsuranceConstants.transactionTypes
										.reinstatement,
									InsuranceConstants.transactionTypes
										.cancellation
								],
								true)
					}
				},
				{
					key: 'data.status',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Status',
						placeholder: 'Select transaction status',
						required: false,
						options:
							this.optionsFactory.mapStringOptions(
								[
									InsuranceConstants.transactionStatusTypes
										.pending,
									InsuranceConstants.transactionStatusTypes
										.application,
									InsuranceConstants.transactionStatusTypes
										.bound,
									InsuranceConstants.transactionStatusTypes
										.issued,
									InsuranceConstants.transactionStatusTypes
										.submitted,
									InsuranceConstants.transactionStatusTypes
										.declined,
									InsuranceConstants.transactionStatusTypes
										.archived,
								],
								true)
					}
				},
				{
					key: 'data.createdBy',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Created By',
						placeholder: 'Select users',
						tooltip:
							'Created by and Changed by selections will include '
								+ 'both result sets.',
						required: false,
						options:
							this.optionsFactory.mapStringOptions(
								[
									userDisplayName
								],
								true)
					}
				},
				{
					key: 'data.changedBy',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Changed By',
						placeholder: 'Select users',
						tooltip:
							'Created by and Changed by selections will include '
								+ 'both result sets.',
						required: false,
						options:
							this.optionsFactory.mapStringOptions(
								[
									userDisplayName
								],
								true)
					}
				},
				{
					key: 'data.keywordFilter',
					type: FormlyConstants.customControls.input,
					id: AppConstants.commonTableActions.filterInput,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Search',
						placeholder: 'Search insured or policy data',
						tooltip: AppConstants.keywordTooltip,
						attributes: {
							title: AppConstants.commonTableActions.filterInput
						},
						keypress:
							(_field: FormlyFieldConfig,
								event: KeyboardEvent) =>
							{
								if (event.key ===
									AppConstants.keyBoardKeyConstants.enter)
								{
									this.search();
								}
							}
					}
				}
			];

		for (const transactionEntityType of
			this.availableTransactionEntityTypes)
		{
			this.latestTransactionsByProduct.push(
				<IKeyValuePair>
				{
					key: transactionEntityType.group,
					value: null
				});
		}

		// If this is the first load, select all.
		if (this.dataSet.data.selectedTransactionEntityTypes.length === 0)
		{
			this.dataSet.data.selectedTransactionEntityTypes =
				this.availableTransactionEntityTypes.map(
					(transactionEntityType: IEntityType) =>
						transactionEntityType.group);
		}

		this.tableFilterQuery = this.compilePrimaryFilter();

		this.loadingTransactionTypes = false;

		let displayOrder: number = 1;
		this.availableColumns =
			<ICommonTableColumn[]>
			[
				{
					dataKey: 'data.type',
					columnHeader: 'Type',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.status',
					columnHeader: 'Status',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.productName',
					columnHeader: 'Product',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.policyNumber',
					columnHeader: 'Policy Number',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						interestCharacteristics
							+ '.name.displayName',
					columnHeader: 'Insured Name',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						assetCharacteristics
							+ '.addresses[0].address',
					columnHeader: 'Address',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						assetCharacteristics
							+ '.addresses[0].city',
					columnHeader: 'City',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						assetCharacteristics
							+ '.addresses[0].state',
					columnHeader: 'State',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						interestCharacteristics
							+ '.phones[0].number',
					columnHeader: 'Mobile Phone',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						interestCharacteristics
							+ '.emails[0].address',
					columnHeader: 'Email',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						'data.termEffectiveDate',
					columnHeader: 'Term Effective Date',
					dataFormatType: AppConstants.dataFormatTypes.date,
					displayOrder: displayOrder++
				},
				{
					dataKey:
						'data.paymentPlanId',
					columnHeader: 'Payment Plan',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						'data.renewalTerm',
					columnHeader: 'Renewal Term',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						'data.writtenTotal',
					columnHeader: 'Written Total',
					dataFormatType: AppConstants.dataFormatTypes.currency,
					displayOrder: displayOrder++
				}
			];

		this.selectedColumns =
			<ICommonTableColumn[]>
			[
				{
					dataKey: 'data.policyNumber',
					columnHeader: 'Policy Number',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						interestCharacteristics
							+ '.name.displayName',
					columnHeader: 'Insured Name',
					displayOrder: displayOrder++
				},
				{
					dataKey:
						assetCharacteristics
							+ '.addresses[0].address',
					columnHeader: 'Address',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.productName',
					columnHeader: 'Product',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.type',
					columnHeader: 'Type',
					displayOrder: displayOrder++
				},
				{
					dataKey: 'data.status',
					columnHeader: 'Status',
					displayOrder: displayOrder++
				}
			];
	}

	/**
	 * Sets up the list column definitions for the current
	 * operation definition object list.
	 *
	 * @memberof InsurancePolicySearchComponent
	 */
	public setupTableDefinitions(): void
	{
		this.policySearchTableDefinitions =
			<ICommonTable>
			{
				tableTitle: 'Policies',
				exportable: true,
				enableFullDatasetExport: false,
				exportMappingDataPromise:
					async(selectedColumns: ICommonTableColumn[]) =>
						this.decoratePolicyTermColumns(
							this.commonTableContext.source.tableData,
							selectedColumns),
				fullPageReservedHeight: this.fullPageReservedHeight,
				objectSearch: {
					filter: this.tableFilterQuery,
					orderBy: this.orderBy,
					offset: 0,
					limit: this.dataLimit,
					virtualIndex: 0,
					virtualPageSize: this.tableRowCount,
					sortField: this.sortField,
					sortOrder: this.sortOrder
				},
				apiPromise:
					async(objectSearch: IObjectSearch) =>
						this.searchMode === true
							? this.apiPromise.bind(this)(objectSearch)
							: [],
				availableColumns: this.availableColumns,
				selectedColumns: this.selectedColumns,
				commonTableContext: (commonTableContext:
					IDynamicComponentContext<CommonTableComponent, any>) =>
				{
					this.commonTableContext = commonTableContext;
				},
				actions: {
					filter: {
						considerFilteringResults: () =>
						{
							document.getElementById(
								AppConstants.commonTableActions.filterInput)
								.focus();
						}
					},
					view: {
						disabledExpandItem: true,
						items: [
							{
								command: () =>
									this.navigateToParent()
							}
						]
					}
				}
			};

		this.loadingTableDefinitions = false;
	}

	/**
	 * Gathers accurate paged and sorted data matching the selected search
	 * filters.
	 *
	 * @async
	 * @param {IObjectSearch} objectSearch
	 * The object search to gather data for.
	 * @returns {IEntityInstance[]}
	 * An awaitable array of policy level data rows matching the current
	 * object search.
	 * @memberof InsurancePolicySearchComponent
	 */
	private async apiPromise(
		objectSearch: IObjectSearch): Promise<IEntityInstance[]>
	{
		const selectedTransactionEntityTypes: string[] =
			this.dataSet.data.selectedTransactionEntityTypes;
		let overallTransactionInstances: IEntityInstance[] = [];
		const existingPolicies: IEntityInstance[] =
			AnyHelper.isNull(this.commonTableContext)
				? []
				: this.commonTableContext.source.tableData;

		if (existingPolicies.length === 0)
		{
			this.clearLatestTransactionsByProduct();
		}

		const existingPolicyNumbers: string[] =
			existingPolicies.map(
				(listItem: IEntityInstance) =>
					listItem.data.policyNumber);

		for (const selectedEntityType of
			selectedTransactionEntityTypes)
		{
			const splitGroups: string[] =
				selectedEntityType
					.split(AppConstants.characters.period);
			const productName =
				splitGroups[splitGroups.length - 1];

			const latestTransactionFilter: string =
				this.compileLatestTransactionFilter(
					selectedEntityType,
					existingPolicies);

			let combinedFilter: string = AppConstants.empty;
			switch (true)
			{
				case AnyHelper.isNullOrWhitespace(objectSearch.filter)
					&& AnyHelper.isNullOrWhitespace(latestTransactionFilter):
					combinedFilter = AppConstants.empty;
					break;
				case AnyHelper.isNullOrWhitespace(objectSearch.filter)
					&& !AnyHelper.isNullOrWhitespace(latestTransactionFilter):
					combinedFilter = latestTransactionFilter;
					break;
				case !AnyHelper.isNullOrWhitespace(objectSearch.filter)
					&& AnyHelper.isNullOrWhitespace(latestTransactionFilter):
					combinedFilter = objectSearch.filter;
					break;
				case !AnyHelper.isNullOrWhitespace(objectSearch.filter)
					&& !AnyHelper.isNullOrWhitespace(latestTransactionFilter):
					combinedFilter =
						`(${objectSearch.filter}) and `
							+ `(${latestTransactionFilter})`;
					break;
			}

			let transactionInstances: IEntityInstance[] =
				await this.loadRecursiveUniqueValues(
					selectedEntityType,
					existingPolicyNumbers,
					combinedFilter,
					this.exactPropertyOrderBy,
					0,
					objectSearch.limit);

			transactionInstances =
				transactionInstances.slice(
					0,
					objectSearch.limit);

			transactionInstances
				.forEach(
					(transaction: IEntityInstance) =>
					{
						transaction.data.productName =
							productName;

						transaction.data.entityTypeGroup =
							productName;
					});

			transactionInstances =
				await this.decoratePolicyTermColumns(
					transactionInstances);

			overallTransactionInstances =
				overallTransactionInstances.concat(
					transactionInstances);
		}

		overallTransactionInstances =
			ObjectHelper.handleOrderBySort(
				overallTransactionInstances,
				this.exactPropertyOrderBy)
				.slice(
					0,
					objectSearch.limit);

		for (const selectedEntityType of
			selectedTransactionEntityTypes)
		{
			this.updateLatestTransactionsByProduct(
				selectedEntityType,
				overallTransactionInstances.filter(
					(entityInstance: IEntityInstance) =>
						entityInstance.entityType ===
							selectedEntityType));
		}

		return overallTransactionInstances;
	}

	/**
	 * Given a set of transaction instances, this will confirm if there are
	 * policy term columns that require separate data loads. If any term
	 * columns are selected, this will load the policy term data and decorate
	 * the transaction instances.
	 *
	 * @async
	 * @param {IEntityInstance[]} transactionInstances
	 * The set of transaction instances to be decorated with term data if
	 * required.
	 * @param {ICommonTableColumn[]} selectedColumns
	 * TIf sent, this will be the set of columns that are being displayed or
	 * exported. If not sent, this will default to the visible columns of the
	 * source table.
	 * @returns {IEntityInstance[]}
	 * An awaitable array of transaction instances with decorated policy term
	 * data.
	 * @memberof InsurancePolicySearchComponent
	 */
	private async decoratePolicyTermColumns(
		transactionInstances: IEntityInstance[],
		selectedColumns: ICommonTableColumn[] = null):
		Promise<IEntityInstance[]>
	{
		if (transactionInstances.length === 0
			|| !AnyHelper.isNullOrWhitespace(
				transactionInstances[0]?.data.renewalTerm))
		{
			return transactionInstances;
		}

		const columnsToDecorate: ICommonTableColumn[] =
			selectedColumns
				?? this.commonTableContext.source
					?.visibleColumns;
		const needsParentTerm: boolean =
			columnsToDecorate
				?.some(
					(column: any) =>
						this.termColumnKeys.has(column.dataKey));

		if (needsParentTerm === false)
		{
			return transactionInstances;
		}

		const transactionIds: number[] =
			transactionInstances.map(
				(transaction: IEntityInstance) =>
					transaction.id);

		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups.policyTerms;
		const filterString: string =
			`Children.Any(ChildInstanceId in (${transactionIds}))`;
		const policyTerms: IEntityInstance[] =
			await ApiHelper.getFullDataSet(
				this.entityInstanceApiService,
				filterString,
				AppConstants.empty,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms);

		transactionInstances.forEach(
			(transaction: IEntityInstance) =>
			{
				const matchingTerm: IEntityInstance =
					policyTerms.find((term: any) =>
						term.data.policyNumber ===
							transaction.data.policyNumber);

				if (matchingTerm)
				{
					transaction.data.termEffectiveDate =
							matchingTerm.data?.effectiveDate;

					transaction.data.renewalTerm =
							matchingTerm.data?.renewalTerm;

					transaction.data.writtenTotal =
							matchingTerm.data?.accounting
								?.writtenTotal;

					transaction.data.paymentPlanId =
							StringHelper.splitNumbersAndWords(
								matchingTerm.data?.accounting
									?.paymentSchedule?.paymentPlan?.id);
				}
			});

		return transactionInstances;
	}

	/**
	 * Recursively queries for values matching the object search specific
	 * against the sent entity type. This will load values recursively until
	 * the full limit worth of values are both found and unique.
	 *
	 * @async
	 * @param {string} selectedEntityType
	 * The entity type to gather object search filtered values for.
	 * @param {string[]} existingPolicyNumbers
	 * The currently loaded and displayed unique policy numbers.
	 * @param {string} filter
	 * The filter to limit possible returned data results.
	 * @param {string} orderBy
	 * The order by used for returning these paged data results.
	 * @param {string} offset
	 * The offset to start from when returning data values.
	 * @param {string} limit
	 * The limit to gather data for.
	 * @param {IEntityInstance[]} existingPolicies
	 * If sent, this will be the current recursive set of unique policy number
	 * based data rows. This should only be sent inside of this method.
	 * @returns {IEntityInstance[]}
	 * An awaitable array of policy level data rows matching the current
	 * object search, unique by policy number.
	 * @memberof InsurancePolicySearchComponent
	 */
	private async loadRecursiveUniqueValues(
		selectedEntityType: string,
		existingPolicyNumbers: string[],
		filter: string,
		orderBy: string,
		offset: number,
		limit: number,
		existingPolicies: IEntityInstance[] = []): Promise<IEntityInstance[]>
	{
		this.entityInstanceApiService
			.entityInstanceTypeGroup = selectedEntityType;
		const policies: IEntityInstance[] =
			await this.entityInstanceApiService
				.query(
					filter,
					orderBy,
					offset,
					limit);

		const uniquePolicies: IEntityInstance[] =
			policies
				.filter(
					(item: IEntityInstance) =>
					{
						const policyNumber: string =
							item.data.policyNumber;
						const firstInstance: boolean =
							existingPolicyNumbers.indexOf(
								policyNumber) === -1;

						if (firstInstance === true)
						{
							existingPolicyNumbers.push(
								policyNumber);
						}

						return firstInstance;
					});

		// Handle a partial load or a fully loaded original limit request.
		if (policies.length < limit
			|| existingPolicies.concat(
				uniquePolicies).length >= limit)
		{
			return existingPolicies
				.concat(uniquePolicies);
		}

		uniquePolicies.forEach(
			(item: IEntityInstance) =>
			{
				existingPolicyNumbers.push(
					item.data.policyNumber);
			});

		return await this.loadRecursiveUniqueValues(
			selectedEntityType,
			existingPolicyNumbers,
			filter,
			orderBy,
			offset + policies.length,
			limit,
			existingPolicies
				.concat(uniquePolicies));
	}

	/**
	 * Returns the matching latest transaction by product key value pair.
	 *
	 * @param {string} entityType
	 * The entity type to find the latest transaction by product for.
	 * @returns {IKeyValuePair}
	 * The existing latest transaction by product key value pair matching the
	 * sent entity type.
	 * @memberof InsurancePolicySearchComponent
	 */
	private getLatestTransactionByProduct(
		entityType: string): IKeyValuePair
	{
		return this.latestTransactionsByProduct.filter(
			(item: IKeyValuePair) =>
				item.key === entityType)[0];
	}

	/**
	 * Resets any storage based data for this common table which is used when
	 * we change the order by, filter set, or selected transaction
	 * entity types.
	 *
	 * @param {string} entityType
	 * The entity type to update the latest transaction by product for.
	 * @param {IEntityInstance[]} transactionInstances
	 * The set of existing transaction instances that match this sent entity
	 * type.
	 * @memberof InsurancePolicySearchComponent
	 */
	private updateLatestTransactionsByProduct(
		entityType: string,
		transactionInstances: IEntityInstance[]): void
	{
		const latestTransactionByProduct: IKeyValuePair =
			this.getLatestTransactionByProduct(
				entityType);

		if (transactionInstances.length > 0)
		{
			latestTransactionByProduct.value =
				transactionInstances[
					transactionInstances.length - 1].id;

			return;
		}

		latestTransactionByProduct.value = null;
	}

	/**
	 * Clears the latest transaction for all products. This ensures that
	 * when the API Promise is called again, it will not use the previous
	 * latest transaction ID as a filter.
	 *
	 * @memberof InsurancePolicySearchComponent
	 */
	private clearLatestTransactionsByProduct(): void
	{
		const selectedTransactionEntityTypes: string[] =
			this.dataSet.data.selectedTransactionEntityTypes;

		for (const selectedEntityType of
			selectedTransactionEntityTypes)
		{
			const latestTransactionByProduct: IKeyValuePair =
				this.getLatestTransactionByProduct(
					selectedEntityType);

			latestTransactionByProduct.value = null;
		}
	}

	/**
	 * Compiles and returns the primary filter for the policy search.
	 *
	 * @returns {string}
	 * The primary search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compilePrimaryFilter(): string
	{
		const activeFilters: string[] =
			[
				this.compileCreatedOrChangedByFilter(),
				this.compileTypeFilter(),
				this.compileStatusFilter(),
				this.compileKeywordsFilter(),
				this.compileDateRangeFilter()
			].filter(
				(filterItem: string) =>
					!AnyHelper.isNullOrWhitespace(filterItem));

		let overallFilter: string = AppConstants.empty;
		for (const filter of activeFilters)
		{
			overallFilter +=
				AnyHelper.isNullOrWhitespace(overallFilter)
					? `(${filter})`
					: ` and (${filter})`;
		}

		return overallFilter;
	}

	/**
	 * Compiles and returns the date range filter for the policy search.
	 *
	 * @returns {string}
	 * The date range search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileDateRangeFilter(): string
	{
		let numberOfDays: number = 0;
		switch (this.dataSet.data.createdDate)
		{
			case AppConstants.dateLimits.pastThirtyDays:
				numberOfDays = 30;
				break;
			case AppConstants.dateLimits.pastNinetyDays:
				numberOfDays = 90;
				break;
			case AppConstants.dateLimits.pastOneHundredAndEightyDays:
				numberOfDays = 180;
				break;
		}

		return this.getFilterValue(
			this.dataSet.data.createdDate,
			`${AppConstants.commonProperties.createDate} ge `
				+ `'${DateHelper
					.getSystemDateTime()
					.minus({ day:  numberOfDays })
					.startOf(<DateTimeUnit>DateHelper.timeUnits.day)
					.toISO()}'`);
	}

	/**
	 * Compiles and returns the type filter for the policy search.
	 *
	 * @returns {string}
	 * The type search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileTypeFilter(): string
	{
		const typeFilter: string =
			this.getFilterValue(
				this.dataSet.data.type,
				`${AppConstants.commonProperties.type} eq `
					+ `'${this.dataSet.data.type}'`);

		return AnyHelper.isNullOrWhitespace(typeFilter)
			? `${AppConstants.commonProperties.type} `
				+ `ne '${InsuranceConstants.transactionTypes.reversal}'`
			: `${typeFilter}`;
	}

	/**
	 * Compiles and returns the status filter for the policy search.
	 *
	 * @returns {string}
	 * The status search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileStatusFilter(): string
	{
		const statusFilter: string =
			this.getFilterValue(
				this.dataSet.data.status,
				`${AppConstants.commonProperties.status} eq `
					+ `'${this.dataSet.data.status}'`);

		return AnyHelper.isNullOrWhitespace(statusFilter)
			? `${AppConstants.commonProperties.status} ne `
				+ `'${InsuranceConstants.transactionStatusTypes.archived}'`
				+ ` and ${AppConstants.commonProperties.status} ne `
				+ `'${InsuranceConstants.transactionStatusTypes.obsolete}'`
			: statusFilter;
	}

	/**
	 * Compiles and returns the created by or changed by filter for the
	 * policy search.
	 *
	 * @returns {string}
	 * The created by or changed by range search filter matching current
	 * selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileCreatedOrChangedByFilter(): string
	{
		const createdByFilter: string =
			this.getFilterValue(
				this.dataSet.data.createdBy,
				`${AppConstants.commonProperties.createdById} eq `
					+ `${this.sessionService.user.id}`);
		const changedByFilter: string =
			this.getFilterValue(
				this.dataSet.data.changedBy,
				`${AppConstants.commonProperties.changedById} eq `
					+ `${this.sessionService.user.id}`);

		switch (true)
		{
			case AnyHelper.isNullOrWhitespace(createdByFilter)
				&& AnyHelper.isNullOrWhitespace(changedByFilter):
				return AppConstants.empty;
			case AnyHelper.isNullOrWhitespace(createdByFilter)
				&& !AnyHelper.isNullOrWhitespace(changedByFilter):
				return changedByFilter;
			case !AnyHelper.isNullOrWhitespace(createdByFilter)
				&& AnyHelper.isNullOrWhitespace(changedByFilter):
				return createdByFilter;
			case !AnyHelper.isNullOrWhitespace(createdByFilter)
				&& !AnyHelper.isNullOrWhitespace(changedByFilter):
				return `${createdByFilter} or ${changedByFilter}`;
		}

		return AppConstants.empty;
	}

	/**
	 * Compiles and returns the keywords filter for the policy search.
	 *
	 * @returns {string}
	 * The keywords search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileKeywordsFilter(): string
	{
		const keywordFilter: string =
			this.dataSet.data.keywordFilter;

		if (AnyHelper.isNullOrWhitespace(keywordFilter))
		{
			return AppConstants.empty;
		}

		let searchArray: string[] =
			keywordFilter.split(
				AppConstants.characters.space);
		searchArray =
			searchArray.map(
				(word: string) =>
					AppConstants.commonProperties.keywords
						+ `.Contains('${word}')`);

		return searchArray.join(' and ');
	}

	/**
	 * Compiles and returns the latest transaction filter for the policy search.
	 *
	 * @param {string} entityType
	 * The entity type to compile a latest transaction filter for.
	 * @param {IEntityInstance[]} existingPolicies
	 * The set of existing policies used to ensure we filter for latest.
	 * @returns {string}
	 * The latest transaction search filter matching current selection values.
	 * @memberof InsurancePolicySearchComponent
	 */
	private compileLatestTransactionFilter(
		entityType: string,
		existingPolicies: IEntityInstance[]): string
	{
		const latestTransactionByProduct: IKeyValuePair =
			this.getLatestTransactionByProduct(
				entityType);

		if (AnyHelper.isNull(latestTransactionByProduct.value))
		{
			return AppConstants.empty;
		}

		if (this.sortField !== AppConstants.commonProperties.id)
		{
			const apiSortField: string =
				this.sortField.replace(
					AppConstants.nestedDataIdentifier,
					AppConstants.empty);

			const latestPolicyTransaction: IEntityInstance =
				existingPolicies.find(
					(item: IEntityInstance) =>
						item.id === latestTransactionByProduct.value);

			const sortValue: string =
				get(
					latestPolicyTransaction,
					'data.' + this.sortField);

			const sortComparator: string =
				this.sortOrder === -1
					? AppConstants.filterQueryOperators.lessThan
					: AppConstants.filterQueryOperators.greaterThan;

			return `(${apiSortField} eq '${sortValue}' `
				+ `and id lt ${latestTransactionByProduct.value}) or `
				+ `(${apiSortField} ${sortComparator} '${sortValue}')`;
		}

		return `id lt ${latestTransactionByProduct.value}`;
	}

	/**
	 * Returns the filter value or an empty string if the sent value is null,
	 * whitespace, or any.
	 *
	 * @param {any} value
	 * The value to check for a required filter string.
	 * @param {string} filterValue
	 * The filter value to return if the sent value is not null, whitespace,
	 * or any.
	 * @returns {string}
	 * The calculated filter value.
	 * @memberof InsurancePolicySearchComponent
	 */
	private getFilterValue(
		value: any,
		filterValue: string): string
	{
		return AnyHelper.isNullOrWhitespace(value)
			|| value === AppConstants.any
			? AppConstants.empty
			: filterValue;
	}

	/**
	 * Updates the url query string to store currently selected transaction
	 * entity types, the keyword filter, and order by values.
	 *
	 * @memberof InsurancePolicySearchComponent
	 */
	private updateUrlQuery(): void
	{
		const queryParameters: any =
			<any>
			{
				selectedTransactionEntityTypes:
					this.dataSet.data.selectedTransactionEntityTypes,
				createdDate:
					this.dataSet.data.createdDate,
				status:
					this.dataSet.data.status,
				type:
					this.dataSet.data.type,
				changedBy:
					this.dataSet.data.changedBy,
				createdBy:
					this.dataSet.data.createdBy,
				keywordFilter:
					this.dataSet.data.keywordFilter,
				orderBy:
					this.orderBy,
				exactPropertyOrderBy:
					this.exactPropertyOrderBy,
				searchMode:
					 this.searchMode
			};

		this.location
			.replaceState(
				this.router
					.createUrlTree(
						[],
						<UrlCreationOptions>
						{
							replaceUrl: true,
							queryParams: {
								routeData:
									ObjectHelper.mapRouteData(queryParameters)
							}
						})
					.toString());
	}

	/**
	 * Navigates to the selected data row policy term page.
	 *
	 * @async
	 * @memberof InsurancePolicySearchComponent
	 */
	private async navigateToParent(): Promise<void>
	{
		const transactionInstance: IEntityInstance =
			this.commonTableContext.source.selectedItem;
		this.entityInstanceApiService
			.entityInstanceTypeGroup =
				transactionInstance.entityType;
		const routeModule: string =
			AppConstants.moduleNames.policy
				+ '/entities';
		const routeType: string = AppConstants.viewTypes.edit;
		const routeLayoutType: string =
			transactionInstance.data.status ===
				InsuranceConstants
					.transactionStatusTypes.quote
				|| (transactionInstance.data.status ===
					InsuranceConstants.transactionStatusTypes.pending
						&& transactionInstance.data.type !==
							InsuranceConstants.transactionTypes.renewal
						&& transactionInstance.data.type !==
							InsuranceConstants.transactionTypes.endorsement)
				? AppConstants
					.layoutTypes
					.quickQuote
				: AppConstants
					.layoutTypes
					.full;
		let routeId: number;
		let routeTypeGroup: string;

		if (transactionInstance.data.status !==
				InsuranceConstants.transactionStatusTypes.quote
			&& transactionInstance.data.status !==
				InsuranceConstants.transactionStatusTypes.pending
			&& transactionInstance.data.status !==
				InsuranceConstants.transactionStatusTypes.application)
		{
			const policyTermParents: IEntityInstance[] =
				await this.entityInstanceApiService
					.getParents(
						transactionInstance.id,
						AppConstants.empty,
						AppConstants.empty,
						0,
						1,
						InsuranceConstants
							.insuranceEntityTypeGroups
							.policyTerms);

			routeId = policyTermParents[0].id;
			routeTypeGroup = InsuranceConstants
				.insuranceEntityTypeGroups
				.policyTerms;
		}
		else
		{
			routeId = transactionInstance.id;
			routeTypeGroup = transactionInstance.entityType;
		}

		this.router.navigate(
			[
				routeModule,
				routeTypeGroup,
				routeType,
				routeId
			],
			{
				queryParams:
				{
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType: routeLayoutType
							})
				}
			});
	}
}